import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";

const HERO_IMAGE = "/gallery/work-1.webp";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.12 * i, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => (
  <section data-testid="hero-section" className="relative overflow-hidden pb-24 pt-32 md:pb-32 md:pt-44">
    <div className="pointer-events-none absolute -right-40 -top-40 h-[480px] w-[480px] rounded-full bg-brand-accent/10 blur-3xl" />
    <div className="pointer-events-none absolute -left-32 top-64 h-[360px] w-[360px] rounded-full bg-brand-muted blur-3xl" />
    <div className="relative mx-auto grid max-w-7xl items-center gap-16 px-6 md:px-12 lg:grid-cols-12">
      <div className="lg:col-span-7">
        <motion.p
          custom={0}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mb-6 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-brand-accent"
        >
          <Sparkles className="h-4 w-4" /> Nail Salon
        </motion.p>
        <motion.h1
          custom={1}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          data-testid="hero-headline"
          className="font-display text-5xl font-medium leading-[1.05] tracking-tighter text-brand-ink md:text-6xl lg:text-7xl"
        >
          Quality that lasts. Nails that stay perfect.
        </motion.h1>
        <motion.p
          custom={2}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mt-8 max-w-xl text-base leading-relaxed text-brand-ink/70 md:text-lg"
        >
          Experience a friendly, relaxed atmosphere with personal attention at Teo&rsquo;s Nails — manicures,
          pedicures, gel &amp; acrylics and custom nail art, at great prices for the quality of work.
        </motion.p>
        <motion.div
          custom={3}
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <a
            href="#booking"
            data-testid="hero-booking-button"
            className="group inline-flex items-center gap-2 rounded-full bg-brand-accent px-7 py-3.5 text-sm font-medium text-white shadow-[0_8px_32px_rgba(190,24,93,0.3)] transition-transform duration-300 hover:-translate-y-0.5"
          >
            Book an appointment
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#services"
            data-testid="hero-services-button"
            className="inline-flex items-center gap-2 rounded-full border border-brand-ink/15 bg-white px-7 py-3.5 text-sm font-medium text-brand-ink transition-colors duration-300 hover:border-brand-accent hover:text-brand-accent"
          >
            Explore services
          </a>
        </motion.div>
      </div>
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
        className="relative lg:col-span-5"
      >
        <div className="overflow-hidden rounded-[2rem] shadow-[0_24px_64px_rgba(45,27,34,0.12)]">
          <img
            src={HERO_IMAGE}
            alt="Pink French tip acrylic set by Teo's Nails"
            data-testid="hero-image"
            className="aspect-[3/4] w-full object-cover"
          />
        </div>
        <div className="absolute -bottom-8 -left-8 max-w-[240px] rounded-2xl border border-brand-muted bg-white/80 p-5 shadow-[0_8px_32px_rgba(45,27,34,0.08)] backdrop-blur-xl">
          <p className="font-display text-xl font-semibold text-brand-ink">Weeks of wear</p>
          <p className="mt-1 text-sm leading-relaxed text-brand-ink/70">
            Gel and acrylic sets that stay glossy and perfect for weeks.
          </p>
        </div>
      </motion.div>
    </div>
  </section>
);
